import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["children", "id", "initialExpandedIndex", "collapsible"];

/**
 * Implementation based on:
 * WAI-ARIA: https://www.w3.org/TR/wai-aria-practices-1.2/#accordion
 *
 * Availability of accordion content to assistive technology requires the use of aria-controls and toggling aria-expanded as regions are expanded and collapsed.
 *
 * Behaviour:
 * Each section header is focusable
 * Enter and Space: when focused on a section header, should toggle the visibility of its body
 *
 * Recommended:
 * Up and Down arrows: when focused on a section header should navigate to the next/previous header.
 * This should not change the state of the section (if the section is hidden, it stays hidden).
 */
import React, { forwardRef } from 'react';
import system from '../../system';
import _AccordionItem from './AccordionItem';
export var AccordionItem = _AccordionItem;
var SYSTEM_PROPS = ['marginProps', 'floatProps', 'flexProps'];
var Accordion = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      id = _ref.id,
      initialExpandedIndex = _ref.initialExpandedIndex,
      collapsible = _ref.collapsible,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  var childrenCount = React.Children.count(children);

  var _React$useState = React.useState(initialExpandedIndex),
      expandedIndex = _React$useState[0],
      setExpandedIndex = _React$useState[1];

  var _React$useState2 = React.useState(-1),
      focusIndex = _React$useState2[0],
      setFocusIndex = _React$useState2[1];

  var _system = system(props, SYSTEM_PROPS),
      className = _system[0],
      style = _system[1],
      rest = _system[2];

  var handleExpanded = function handleExpanded(index) {
    if (collapsible) {
      setExpandedIndex(expandedIndex === index ? -1 : index);
    } else {
      setExpandedIndex(index);
    }
  };

  var onClick = function onClick(index) {
    return handleExpanded(index);
  };

  var onFocus = function onFocus(index) {
    return setFocusIndex(index);
  };

  var onKeyDown = function onKeyDown(e) {
    if (e.keyCode === 40) {
      // Down arrow
      e.preventDefault();

      if (focusIndex === childrenCount - 1) {
        setFocusIndex(0);
      } else {
        setFocusIndex(focusIndex + 1);
      }
    } else if (e.keyCode === 38) {
      // Up arrow
      e.preventDefault();

      if (focusIndex === 0) {
        setFocusIndex(childrenCount - 1);
      } else {
        setFocusIndex(focusIndex - 1);
      }
    }
  };

  return /*#__PURE__*/React.createElement("div", _extends({
    id: id,
    className: className,
    style: style,
    ref: ref
  }, rest), React.Children.map(children, function (child, index) {
    if (child.type.identifier !== _AccordionItem.identifier) {
      // eslint-disable-next-line
      console.warn('[INVALID CHILDREN](Accordion): Children of Accordion must be of type AccordionItem.');
      return null;
    }

    return /*#__PURE__*/React.cloneElement(child, {
      id: id + "-" + index,
      key: id + "-" + index,
      expanded: expandedIndex === index,
      focussed: focusIndex === index,
      onKeyDown: onKeyDown,
      onClick: onClick,
      onFocus: onFocus,
      index: index
    }, child.props.children);
  }));
});
Accordion.displayName = 'Accordion';
Accordion.identifier = Accordion.displayName; // used in docs website

Accordion.clonableProps = SYSTEM_PROPS;
Accordion.defaultProps = {
  initialExpandedIndex: -1
};
export default Accordion;